import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../stores/gameStore';
import { getMoveInfo } from '@elmental/shared';
import { haptic } from '../services/telegram';
import { applyResults } from '../services/gameService';
import { playSound } from '../services/audio';
import { currencyForBalanceKind, formatCurrencyAmount } from '../services/economy';
import { TrophyIcon } from '../components/icons/TrophyIcon';
import { SkullIcon } from '../components/icons/SkullIcon';
import { HandshakeIcon } from '../components/icons/HandshakeIcon';
import { CoinsIcon } from '../components/icons/CoinsIcon';
import { SwordsIcon } from '../components/icons/SwordsIcon';
import { HomeIcon } from '../components/icons/HomeIcon';
import { FlameIcon } from '../components/icons/FlameIcon';
import { BoltIcon } from '../components/icons/BoltIcon';
import { MoveArt } from '../components/MoveArt';

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  size: number;
  duration: number;
  delay: number;
}

function generateConfetti(count: number): ConfettiPiece[] {
  const colors = ['#22c55e', '#3b82f6', '#ef4444', '#a855f7', '#f59e0b', '#38bdf8'];
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    color: colors[i % colors.length],
    size: 6 + Math.random() * 8,
    duration: 2 + Math.random() * 2,
    delay: Math.random() * 1.5,
  }));
}

const CONFETTI = generateConfetti(30);

export function ResultScreen() {
  const {
    matchResult,
    opponentName,
    roundHistory,
    resetMatch,
    setScreen,
  } = useGameStore();

  const hasFiredHaptic = useRef(false);

  useEffect(() => {
    if (!matchResult || hasFiredHaptic.current) return;
    hasFiredHaptic.current = true;
    if (matchResult.winner === 'me') {
      haptic.success();
      playSound('matchWin');
    } else if (matchResult.winner === 'opponent') {
      haptic.error();
      playSound('matchLose');
    } else {
      haptic.warning();
      playSound('matchDraw');
    }
  }, [matchResult]);

  const handlePlayAgain = () => {
    haptic.medium();
    applyResults('playAgain');
  };

  const handleHome = () => {
    haptic.light();
    applyResults('home');
  };

  if (!matchResult) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-game-bg p-6 gap-4 text-center">
        <div className="text-text-secondary">Result is still syncing...</div>
        <button data-nav className="btn-ghost w-full max-w-xs py-3 text-sm" onClick={handleHome}>
          Back to Home
        </button>
      </div>
    );
  }

  const isWin = matchResult.winner === 'me';
  const isDraw = matchResult.winner === 'draw';
  const currency = currencyForBalanceKind(matchResult.balanceKind);

  const resultConfig = isWin
    ? {
        label: 'VICTORY!',
        icon: <TrophyIcon size={56} className="text-energy-high" />,
        color: '#22c55e',
        bg: 'linear-gradient(180deg, oklch(22% 0.06 145 / 0.72) 0%, oklch(9% 0.035 252 / 0.86) 62%)',
        subtext: 'Excellent battle!',
      }
    : isDraw
    ? {
        label: 'DRAW',
        icon: <HandshakeIcon size={56} className="text-energy-mid" />,
        color: '#eab308',
        bg: 'linear-gradient(180deg, oklch(22% 0.055 82 / 0.72) 0%, oklch(9% 0.035 252 / 0.86) 62%)',
        subtext: 'A balanced match',
      }
    : {
        label: 'DEFEAT',
        icon: <SkullIcon size={56} className="text-energy-low" />,
        color: '#ef4444',
        bg: 'linear-gradient(180deg, oklch(20% 0.06 31 / 0.72) 0%, oklch(9% 0.035 252 / 0.86) 62%)',
        subtext: 'Better luck next time',
      };

  return (
    <div
      className="game-home-stage relative flex flex-col h-full overflow-y-auto scrollbar-hide"
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: resultConfig.bg }} />
      {/* Confetti for wins */}
      {isWin && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {CONFETTI.map((piece) => (
            <motion.div
              key={piece.id}
              className="absolute rounded-sm"
              style={{
                left: `${piece.x}%`,
                top: '-10px',
                width: piece.size,
                height: piece.size,
                background: piece.color,
              }}
              animate={{
                y: '110vh',
                rotate: [0, 360 * (Math.random() > 0.5 ? 1 : -1)],
                opacity: [1, 1, 0],
              }}
              transition={{
                duration: piece.duration,
                delay: piece.delay,
                ease: 'linear',
              }}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col items-center gap-5 p-5 pt-8">
        {/* Result icon + label */}
        <motion.div
          className="flex flex-col items-center gap-3"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.span
            className="flex items-center justify-center"
            animate={isWin ? { rotate: [0, -10, 10, -5, 5, 0] } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {resultConfig.icon}
          </motion.span>
          <motion.div
            className="text-4xl font-black tracking-wider text-center"
            style={{
              color: resultConfig.color,
              textShadow: `0 0 30px ${resultConfig.color}80`,
            }}
          >
            {resultConfig.label}
          </motion.div>
          <div className="text-sm text-text-secondary">{resultConfig.subtext}</div>
        </motion.div>

        {/* Score summary */}
        <motion.div
          className="arena-score-ribbon flex items-center justify-around"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="text-center">
            <div className="text-xs text-text-secondary mb-1">You</div>
            <div className="text-3xl font-black text-energy-high">{matchResult.myScore}</div>
          </div>
          <div
            className="text-3xl font-black text-text-muted"
            style={{ textShadow: '0 2px 0 oklch(3% 0.02 252 / 0.56)' }}
          >
            vs
          </div>
          <div className="text-center">
            <div className="text-xs text-text-secondary mb-1 truncate max-w-[80px]">{opponentName}</div>
            <div className="text-3xl font-black text-energy-low">{matchResult.opponentScore}</div>
          </div>
        </motion.div>

        {/* Economy + Rating changes */}
        <motion.div
          className="arena-page-section w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-2">
              <CoinsIcon size={18} className="text-gold" />
              <span className="text-sm text-text-secondary">{currency} Spent</span>
            </div>
            <span
              className="text-lg font-black"
              style={{ color: '#ef4444' }}
            >
              {formatCurrencyAmount(matchResult.elmSpent, currency)}
            </span>
          </div>
          <div
            className="my-2 h-px"
            style={{ background: 'rgba(255,255,255,0.06)' }}
          />
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-2">
              <TrophyIcon size={18} className="text-gold" />
              <span className="text-sm text-text-secondary">Earned Season Points</span>
            </div>
            <span className="text-lg font-black text-energy-high">
              +{matchResult.seasonPointsEarned}
            </span>
          </div>
          <div
            className="my-2 h-px"
            style={{ background: 'oklch(43% 0.055 252 / 0.42)' }}
          />
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-2">
              <TrophyIcon size={18} className="text-gold" />
              <span className="text-sm text-text-secondary">Rating Change</span>
            </div>
            <span
              className="text-lg font-black"
              style={{
                color: matchResult.ratingChange >= 0 ? '#22c55e' : '#ef4444',
              }}
            >
              {matchResult.ratingChange >= 0 ? '+' : ''}{matchResult.ratingChange}
            </span>
          </div>
        </motion.div>

        {/* Economy Breakdown */}
        {'entryFee' in matchResult && (
          <motion.div
            className="arena-page-section w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <div className="arena-section-label mb-3">
              Economy Breakdown
            </div>
            <div className="flex flex-col gap-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-text-secondary">Entry Fee</span>
                <span className="text-text-primary">{formatCurrencyAmount(matchResult.entryFee, currency)}</span>
              </div>
              {matchResult.boostCost > 0 && (
                <>
                  <div className="my-1 h-px" style={{ background: 'oklch(43% 0.055 252 / 0.42)' }} />
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Boost Cost</span>
                    <span className="text-text-primary">{formatCurrencyAmount(matchResult.boostCost, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Boost Status</span>
                    <span className="text-energy-low font-bold"><FlameIcon size={14} className="inline" /> Spent</span>
                  </div>
                </>
              )}
              <div className="my-1 h-px" style={{ background: 'oklch(43% 0.055 252 / 0.42)' }} />
              <div className="flex justify-between text-base font-bold">
                <span className="text-text-primary">Net ELM</span>
                <span style={{ color: matchResult.elmDelta >= 0 ? '#22c55e' : '#ef4444' }}>
                  {formatCurrencyAmount(matchResult.elmDelta, currency, { signed: true })}
                </span>
              </div>
              <div className="pt-1 text-xs leading-snug text-text-secondary">
                ELM is the match entry fee. Your earned reward for this match is Season Points.
              </div>
            </div>
          </motion.div>
        )}

        {/* Round history */}
        {roundHistory.length > 0 && (
          <motion.div
            className="arena-page-section w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="arena-section-label mb-3">
              Round History
            </div>
            <div className="flex flex-col gap-1.5">
              {roundHistory.map((round) => (
                <div
                  key={round.round}
                  className="arena-soft-row rounded-xl px-3 py-2 text-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-text-muted text-xs w-7 flex-shrink-0">R{round.round}</span>
                    <div className="flex items-center justify-center gap-2 min-w-0">
                      <div className="flex flex-col items-center gap-0.5">
                        <MoveArt moveId={round.myMove} size="sm" />
                        <span className="text-[10px] font-bold text-text-primary">{getMoveInfo(round.myMove).name}</span>
                      </div>
                      <span className="text-text-muted">vs</span>
                      <div className="flex flex-col items-center gap-0.5">
                        <MoveArt moveId={round.opponentMove} size="sm" />
                        <span className="text-[10px] font-bold text-text-primary">{getMoveInfo(round.opponentMove).name}</span>
                      </div>
                    </div>
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{
                        color: round.result === 'win' ? '#22c55e' : round.result === 'lose' ? '#ef4444' : '#eab308',
                        background: round.result === 'win' ? 'rgba(34,197,94,0.12)' : round.result === 'lose' ? 'rgba(239,68,68,0.12)' : 'rgba(234,179,8,0.12)',
                      }}
                    >
                      {round.result.toUpperCase()}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-text-secondary">Energy played</span>
                    <span className="text-text-primary font-bold inline-flex items-center gap-1">
                      <span className="inline-flex items-center gap-0.5">{round.myEnergyBefore}<BoltIcon size={10} /></span>
                      <span className="text-text-muted">to</span>
                      <span className="inline-flex items-center gap-0.5">{round.myEnergyAfter}<BoltIcon size={10} /></span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Action buttons */}
        <motion.div
          className="flex flex-col gap-3 w-full pb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button
            data-nav
            className="btn-play w-full"
            onClick={handlePlayAgain}
          >
            <span className="flex items-center gap-2 justify-center">
              <SwordsIcon size={20} />
              Play Again
            </span>
          </button>
          <button
            data-nav
            className="btn-ghost w-full py-3 text-sm"
            onClick={handleHome}
          >
            <span className="flex items-center gap-2 justify-center">
              <HomeIcon size={16} />
              Back to Home
            </span>
          </button>
        </motion.div>
      </div>
    </div>
  );
}
